import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import isNil from 'lodash/isNil';
import { ReactNode } from 'react';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';

type Props<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  options: { value: PathValue<TFieldValues, FieldPath<TFieldValues>>; label: ReactNode }[];

  /**
   * if true, the radio group is rendered in a horizontal row
   */
  row?: boolean;
  sx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
  isDisabled?: boolean;
  onValueChange?: (value: PathValue<TFieldValues, FieldPath<TFieldValues>>) => void;
};

export default function RHFRadio<TFieldValues extends FieldValues = FieldValues>({
  name,
  options,
  row,
  sx,
  itemSx,
  isDisabled,
  onValueChange,
}: Props<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <FormControl error={!isNil(error)}>
          <RadioGroup
            {...field}
            row={row}
            sx={{ ...sx }}
            onChange={(event) => {
              const selectedOption = options.find((o) => String(o.value) === event.target.value);
              if (selectedOption !== undefined) {
                field.onChange(selectedOption.value);
                onValueChange?.(selectedOption.value);
              } else {
                field.onChange(event);
              }
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                disabled={isDisabled}
                sx={itemSx}
                key={String(option.value)}
                value={option.value}
                control={<Radio disableRipple />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {!isNil(error) ? <FormHelperText>{error.message}</FormHelperText> : null}
        </FormControl>
      )}
    />
  );
}
